import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { cloneDeep } from 'lodash-es';
import { take, takeWhile } from 'rxjs/operators';
import { Constants } from 'src/app/order/constants/constants';
import { DocumentCategory } from 'src/app/order/enums/document-category.enum';
import { FactorIndexEnum } from 'src/app/order/enums/factor-index.enum';
import { FeeTypeEnum } from 'src/app/order/enums/fee-type.enum';
import { MdrFlexModalityEnum } from 'src/app/order/enums/mdr-flex-modality.enum';
import { ModalDefinitions } from 'src/app/order/enums/modal-definitions.enum';
import { Messages } from 'src/app/order/messages/order.messages';
import { ClientService } from 'src/app/order/services/external/client-validation/client-validation.service';
import { FeeService } from 'src/app/order/services/external/fee/fee.service';
import { ProposalDraftService } from 'src/app/order/services/external/proposal-draft/proposal-draft.service';
import { ProposalService } from 'src/app/order/services/external/proposal/proposal.service';
import { SimulationService } from 'src/app/order/services/external/simulation/simulation.service';
import { DialogService } from 'src/app/order/services/internal/dialog/dialog.service';
import { LoadingService } from 'src/app/order/services/internal/loading/loading.service';
import { RoutingService } from 'src/app/order/services/internal/routing/routing.service';
import { ScrollService } from 'src/app/order/services/internal/scroll/scroll.service';
import { WizardService } from 'src/app/order/services/internal/wizard/wizard.service';
import { DataStoreService } from 'src/app/order/store/data-store.service';
import { DataStore } from 'src/app/order/store/data.store';
import { getValueWithDecimalPlaces } from 'src/app/order/utils/decimal-places';
import { FdFeeEditorConfig } from 'src/app/shared/fd-form-components/fd-fee-editor/fd-fee-editor.component';
import { FdRadioButtonConfig } from 'src/app/shared/fd-form-components/fd-radio-button/fd-radio-button.component';
import { ApiResultModel } from 'src/app/shared/models/api-result.model';
import { FeeCalculationModel } from 'src/app/shared/models/fee-calculation.model';
import { FeeTypeModel, Proposal } from 'src/app/shared/models/proposal';
import { ProposalDraftQueryModel } from 'src/app/shared/models/proposal-draft-query.model';
import { Rate, RatesClientValidation } from 'src/app/shared/models/rates-client-validation.model';
import { FactorFeeModel } from 'src/app/shared/models/response/response-factor-fee.model';
import { MdrFlexFeeModel } from 'src/app/shared/models/response/response-mdr-flex-fee.model';
import { SimulationFeesModel, SimulationModel, SimulationServicesModel } from 'src/app/shared/models/simulation.model';
import { ConfigurationService } from 'src/app/start/services/configuration.service';
import { FormStep } from '../../form-step';
import { FeeEditorDialogData, FeeEditorModalComponent } from '../../modals/fee-editor-modal/fee-editor-modal.component';
import { PrepaymentService } from 'src/app/order/services/external/prepayment/prepayment.service';

interface CellRef {
  index: number;
  model: MdrFlexFeeModel;
}
interface GroupedMdrFlexRow {
  flag: string;
  codeFlag: string | number;
  credito?: CellRef;
  debito?: CellRef;
  parceladoAte6?: CellRef;
  parceladoMais6?: CellRef;
  crediario?: CellRef;
  voucher?: CellRef;
}

@Component({
  selector: 'app-mdr-flex-fee-edit',
  templateUrl: './mdr-flex-fee-edit.component.html',
  styleUrls: ['./mdr-flex-fee-edit.component.scss'],
  standalone: false,
})
export class MdrFlexFeeEditComponent extends FormStep implements OnInit {
  get isCPF() {
    return (
      this.proposal &&
      this.proposal.registrationDataPersonalInfo &&
      this.proposal.registrationDataPersonalInfo.documentCategory === DocumentCategory.CPF
    );
  }
  get isCNPJ() {
    return (
      this.proposal &&
      this.proposal.registrationDataPersonalInfo &&
      this.proposal.registrationDataPersonalInfo.documentCategory === DocumentCategory.CNPJ
    );
  }
  get isCnaeLoterica(): boolean {
    return this.proposal.offerDataDetails.cnae.toString() === '8299706';
  }

  private get factorFeeFormsArray(): UntypedFormArray {
    return this.formControls.factorFeeForms as UntypedFormArray;
  }

  private get mdrFlexFeeFormsArray(): UntypedFormArray {
    return this.formControls.mdrFlexFeeForms as UntypedFormArray;
  }

  private set mdrFlexFeeFormsArray(value: UntypedFormArray) {
    this.formControls.mdrFlexFeeForms = value;
  }

  public get factorFeeFormsControls() {
    return this.factorFeeFormsArray.controls;
  }

  public get mdrFlexFeeFormsControls() {
    return this.mdrFlexFeeFormsArray.controls as UntypedFormGroup[];
  }

  @ViewChild('factorRow') public factorRow: ElementRef;
  @ViewChild('mdrFlexRow') public mdrFlexRow?: ElementRef;

  public factor1: number;
  public factor2: number;
  public proposal: Proposal;
  public editedModel: MdrFlexFeeModel[] = [];
  public proposalMdrFlexModel: MdrFlexFeeModel[] = [];
  public proposalFactorModel: FactorFeeModel[] = [];
  public dialogClosed: boolean = true;

  public readonly UNDER_MINIMUM_LIMIT_TEXT =
    'Devido ao fato da taxa informada ser menor que a taxa mínima, a proposta poderá não ser submetida ou aprovada';

  public mdrFlexLoaded = false;
  public mdrFlexLoading = false;

  public displayedFactorColumns = ['factorFlag', 'factorTransaction', 'factorFee'];

  public displayedMdrFlexColumns = ['flag', 'credito', 'debito', 'parceladoAte6', 'parceladoMais6', 'crediario'];

  private readonly BASE_MDR_FLEX_COLUMNS: ReadonlyArray<string> = [
    'flag',
    'credito',
    'debito',
    'parceladoAte6',
    'parceladoMais6',
    'crediario',
  ];

  public feeCalculation: FeeCalculationModel[] = [];
  public STEP_NUMBER = 0;
  public isCEF = this.dataStoreService.getCEFFlag();

  public factorFees: FactorFeeModel[];
  public mdrFlexFees: MdrFlexFeeModel[];

  public mdrFlexDataSource: MatTableDataSource<MdrFlexFeeModel> = new MatTableDataSource<MdrFlexFeeModel>([]);
  public mdrFlexGroupedDataSource: MatTableDataSource<GroupedMdrFlexRow> = new MatTableDataSource<GroupedMdrFlexRow>([]);
  public factorDataSource: MatTableDataSource<FactorFeeModel> = new MatTableDataSource<FactorFeeModel>([]);

  @ViewChild('tooltip') public tooltip: MatTooltip;

  public fieldsArray: any[] = [];
  public additionalPerInstallment: MdrFlexFeeModel | null = null;
  public additionalPerInstallmentIndex: number | null = null;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private feeService: FeeService,
    protected proposalService: ProposalService,
    protected loadingService: LoadingService,
    protected dialogService: DialogService,
    protected dataStore: DataStore,
    protected scrollService: ScrollService,
    protected wizardService: WizardService,
    protected matDialog: MatDialog,
    protected configurationService: ConfigurationService,
    protected routingService: RoutingService,
    protected dataStoreService: DataStoreService,
    private clientService: ClientService,
    private simulationService: SimulationService,
    private proposalDraftService: ProposalDraftService,
    private prepaymentService: PrepaymentService
  ) {
    super(
      dataStore,
      scrollService,
      wizardService,
      matDialog,
      proposalService,
      loadingService,
      dialogService,
      configurationService,
      routingService,
      dataStoreService
    );

    const timer = setInterval(() => {
      const factorOk = !!this.factorRow?.nativeElement;
      const mdrFlexOk = true;
      if (factorOk && mdrFlexOk) clearInterval(timer);
    }, 50);
  }

  public isPfacAccreditationType(): boolean {
    return this.proposal?.registrationDataPersonalInfo?.isPfac === true;
  }

  public getLabel() {
    return this.isCEF ? 'Taxas MDR com Antecipação' : 'Taxas MDR Flex';
  }

  private formatPercentSafe(value: any): string {
    const num = Number(value);
    if (value === null || value === undefined || Number.isNaN(num)) return '--';
    return `${getValueWithDecimalPlaces(num)}%`;
  }
  public displayFee(model?: MdrFlexFeeModel): string {
    if (!model) return '--';
    const preferred = model.calculatedMdrFlexFee ?? model.suggestedMdrFlexFee;
    return this.formatPercentSafe(preferred);
  }

  public saveSimulation() {
    if (!this.isFormGroupValid()) return;

    this.dataStore.setStepStatus(this.STEP_NUMBER, this.formGroup.valid);
    this.persistData().then((persisted) => {
      if (persisted) {
        this.dataStore
          .getProposal()
          .pipe(take(1))
          .pipe(takeWhile(() => this.alive))
          .toPromise()
          .then(
            (proposal) => {
              this.proposal = proposal;
              this.submitSimulation();
            },
            (_) => {}
          );
      }
    });
  }

  public buildFeeObject(): SimulationFeesModel[] {
    return this.proposal.mdrFlexFeeModel.map(
      (item) =>
        ({
          idService: item.serviceId && item.serviceId.toString(),
          minimumMdrFlexFee: item.minimumMdrFlexFee,
          sentMdrFlexFee: item.mdrFlexFeeEdit,
          suggestedMdrFlexFee: item.suggestedMdrFlexFee,
          modality: item.modality,
          transactionDescription: item.transaction,
        } as SimulationFeesModel)
    );
  }

  public buildServiceObject(): SimulationServicesModel[] {
    return this.proposal.productSelection.products.map(
      (item) =>
        ({
          idService: item.technology,
          technologyAmount: item.amount,
          technologyPrice: Number(item.price.toString().replace(/[Rr$\s]/g, '')),
        } as SimulationServicesModel)
    );
  }

  public deleteDraft() {
    const activeDraft = localStorage.getItem(Constants.ACTIVE_DRAFT_SESSIONSTORAGE_KEY);
    this.proposalDraftService.deleteDraft(this.createRequestObject(activeDraft)).subscribe(
      (_) => {
        this.dialogService.openDialog(Messages.SIMULATION_SAVE_SUCCESS, () => this.routingService.navigateToHome());
      },
      (error) => {
        let errorArg = error;
        if (error.error && typeof error.error === 'object') {
          errorArg = error.error;
          if (errorArg.field) errorArg = errorArg.field;
          else if (error.error instanceof ProgressEvent) errorArg = error.message;
        }
        this.dialogService.openErrorDialog(Messages.SIMULATION_SAVE_ERROR, errorArg, () => this.routingService.navigateToHome());
      }
    );
  }

  public createRequestObject(externalDraftUuid): ProposalDraftQueryModel {
    return { externalDraftUuid };
  }

  public submitSimulation() {
    const userData = this.dataStoreService.getUserData();
    if (!userData) return;

    const model: SimulationModel = {
      agentCpfCnpj: userData.cpfCnpj,
      billingValue: this.proposal.offerDataDetails.annualRevenue,
      cnaeNumber: Number(this.proposal.offerDataDetails.cnae),
      cpfCnpj: this.proposal.registrationDataPersonalInfo.cpfCnpj,
      email: this.isCPF ? this.proposal.registrationDataPersonalInfo.personEmail : this.proposal.registrationDataPersonalInfo.companyEmail,
      fees: this.buildFeeObject(),
      services: this.buildServiceObject(),
    };

    this.proposal.factorFeeModel.map((item) => {
      (model.factorType = item.factor),
        (model.minimumFactorFee = item.minimumFactorFee),
        (model.suggestedFactorFee = item.suggestedFactorFee);
    });

    this.simulationService.saveSimulation(model).subscribe(
      (_) => this.deleteDraft(),
      (error) => {
        let errorArg = error;
        if (error.error && typeof error.error === 'object') {
          errorArg = error.error;
          if (errorArg.field) errorArg = errorArg.field;
          else if (error.error instanceof ProgressEvent) errorArg = error.message;
        }
        this.dialogService.openErrorDialog(Messages.SIMULATION_SAVE_ERROR, errorArg, () => this.routingService.navigateToHome());
      }
    );
  }

  public showTooltipAndStopPropagation() {
    this.tooltip.toggle();
  }

  public shouldShowAlert(i: number) {
    const formGroup = this.mdrFlexFeeFormsControls[i];
    return (
      this.dialogClosed &&
      formGroup &&
      formGroup.controls &&
      formGroup.controls.suggestedMdrFlexFee &&
      formGroup.controls.suggestedMdrFlexFee.value !== null &&
      formGroup.controls.suggestedMdrFlexFee.value !== undefined &&
      formGroup.controls.suggestedMdrFlexFee.value !== '' &&
      formGroup.controls.minimumMdrFlexFee &&
      formGroup.controls.suggestedMdrFlexFee.value < formGroup.controls.minimumMdrFlexFee.value
    );
  }

  public async onMdrFlexModalConfirm(data: FeeEditorDialogData) {
    const response = await this.checkBusinessPoint();
    if (!response) {
      return;
    }
    const relatedFormGroup = this.mdrFlexFeeFormsArray.controls[data.rowIndex] as UntypedFormGroup;

    if (relatedFormGroup) {
      const rawValue = relatedFormGroup.getRawValue();
      if (rawValue && rawValue.suggestedMdrFlexFee) {
        const item = this.mdrFlexFees.find((val, idx) => idx === data.rowIndex);
        data.formControl.setValue(data.formGroup.controls.mdrFlexFeeEdit.value);

        const feeToCalculate: FeeCalculationModel = {
          codeFlag: item.codeFlag,
          factor1: this.factor1,
          factor2: this.factor2,
          fee: data.formControl.value,
          modality: item.modality,
        };

        if (!this.editedModel || !this.editedModel.length) {
          let model = [];
          if (!this.proposalMdrFlexModel || !this.proposalMdrFlexModel.length) model = this.mdrFlexFees;
          else model = this.proposalMdrFlexModel;
          this.editedModel = cloneDeep(model);
        }
        const editedItem = this.editedModel.find(
          (edited) => feeToCalculate.codeFlag === edited.codeFlag && edited.modality === feeToCalculate.modality
        );
        if (editedItem) editedItem.suggestedMdrFlexFee = feeToCalculate.fee;

        this.calculateFees([feeToCalculate], this.editedModel);
      }
    }
  }
  public async onFactorModalConfirm(data: FeeEditorDialogData) {
    const relatedFormGroup = this.factorFeeFormsArray.controls[data.rowIndex] as UntypedFormGroup;
    if (relatedFormGroup) {
      const rawValue = relatedFormGroup.getRawValue();
      if (rawValue && rawValue.suggestedFactorFee !== null && rawValue.suggestedFactorFee !== undefined) {
        const item = this.factorDataSource.data.find((val, idx) => idx === data.rowIndex);
        data.formControl.setValue(data.formGroup.controls.factorFeeEdit.value);
        if (item.factor === FactorIndexEnum.FACTOR_ONE) this.factor1 = data.formControl.value;
        else this.factor2 = data.formControl.value;

        const feesToCalculate = this.mdrFlexFees.map(
          (x: MdrFlexFeeModel): FeeCalculationModel => ({
            codeFlag: x.codeFlag,
            factor1: this.factor1,
            factor2: this.factor2,
            fee: x.suggestedMdrFlexFee,
            modality: x.modality,
          })
        );

        (this.fieldsArray[data.rowIndex].suggestedFactorFee as FdFeeEditorConfig).isEdited = this.isFactorEditedWithValue(
          item,
          data.formControl.value
        );

        this.calculateFees(feesToCalculate, this.mdrFlexDataSource.data);
      }
    }
  }

  private async checkBusinessPoint() {
    let response;
    try {
      response = await this.prepaymentService
        .callCheckBusinessPoint(this.proposal.offerDataDetails.campaign, this.proposal.offerDataDetails.revenueRange)
        .toPromise();
      if (!response) {
        this.dialogService.openDialogWithMessage('Não é possivel seguir com a solicitação, pois para esta campanha os BPS estão zerados.');
        return;
      }
    } catch (err) {
      this.dialogService.openErrorDialog(Messages.GENERIC_ERROR, JSON.stringify(err.error.details), () =>
        this.routingService.navigateToHome()
      );
      response = false;
    }
    return response;
  }

  public updateFields(proposal: Proposal) {
    if (this.isPfacAccreditationType() && proposal?.feeTypeModel?.receivingDeadline) {
      this.formControls.receivingDeadline.setValue(proposal.feeTypeModel.receivingDeadline);
    }

    this.mdrFlexLoaded = false;
    this.mdrFlexLoading = true;

    this.proposalMdrFlexModel = proposal.mdrFlexFeeModel;
    this.proposalFactorModel = proposal.factorFeeModel;

    if (this.proposal && this.proposal.offerDataDetails && this.proposal.offerDataDetails.cnaeChanged) {
      this.proposalMdrFlexModel = [];
      this.proposalFactorModel = [];
    }

    if (this.proposalMdrFlexModel && this.proposalMdrFlexModel.length && this.proposalFactorModel && this.proposalFactorModel.length) {
      this.feeService
        .getFactorFees(
          this.proposal.offerDataDetails.cnae,
          this.proposal.offerDataDetails.revenueRange,
          this.proposal.offerDataDetails.campaign,
          this.proposal.feeTypeModel.receivingDeadline
        )
        .pipe(takeWhile(() => this.alive))
        .subscribe(
          (response) => {
            this.factorFees = response;
            this.processFactorFees(this.proposalFactorModel);
            this.feeService
              .getMdrFlexFees(
                this.proposal.offerDataDetails.cnae,
                this.proposal.registrationDataAddress.state,
                this.proposal.offerDataDetails.campaign,
                this.proposal.offerDataDetails.revenueRange,
                this.proposal.feeTypeModel.receivingDeadline,
                this.proposal.registrationDataPersonalInfo.documentCategory === 'CPF' ? 'P' : 'L'
              )
              .pipe(takeWhile(() => this.alive))
              .subscribe(
                (response) => {
                  if (response && response.length) {
                    this.mdrFlexFees = response;
                    const model = this.proposalMdrFlexModel.map(
                      (item: MdrFlexFeeModel): FeeCalculationModel => ({
                        codeFlag: item.codeFlag,
                        factor1: this.factor1,
                        factor2: this.factor2,
                        fee: item.suggestedMdrFlexFee,
                        modality: item.modality,
                      })
                    );
                    this.calculateFees(model, this.proposalMdrFlexModel);
                  } else {
                    this.mdrFlexLoading = false;
                    this.mdrFlexLoaded = false;
                  }
                },
                (err: HttpErrorResponse) => {
                  this.mdrFlexLoading = false;
                  this.mdrFlexLoaded = false;
                  const serializedError = err.error ? (err.error as ApiResultModel) : null;
                  if (serializedError && serializedError.message) {
                    if (this.isCEF) {
                      this.dialogService.openDialogWithMessage(serializedError.message, () => this.routingService.navigateToHome());
                      return;
                    }
                    this.dialogService.openDialogWithMessage(serializedError.message, () => this.back(false));
                  } else {
                    if (this.isCEF) {
                      this.dialogService.openDialog(Messages.FAILED_TO_GET_MDR_FLEX_FEES, () => this.routingService.navigateToHome());
                      return;
                    }
                    this.dialogService.openDialog(Messages.FAILED_TO_GET_MDR_FLEX_FEES, () => this.back(false));
                  }
                }
              );
          },
          (err: HttpErrorResponse) => {
            this.mdrFlexLoading = false;
            this.mdrFlexLoaded = false;
            const serializedError = err.error ? (err.error as ApiResultModel) : null;
            if (serializedError && serializedError.message) {
              this.dialogService.openDialogWithMessage(serializedError.message, () => this.back());
            } else {
              this.dialogService.openDialog(Messages.FAILED_TO_GET_MDR_FLEX_FACTORS, () => this.back());
            }
          }
        );
    } else {
      this.feeService
        .getFactorFees(
          this.proposal.offerDataDetails.cnae,
          this.proposal.offerDataDetails.revenueRange,
          this.proposal.offerDataDetails.campaign,
          this.proposal.feeTypeModel.receivingDeadline
        )
        .pipe(takeWhile(() => this.alive))
        .subscribe(
          (response) => {
            if (response && response.length) {
              this.factorFees = response;
              this.processFactorFees(response);
              this.getMdrFlexFees();
            } else {
              this.mdrFlexLoading = false;
              this.mdrFlexLoaded = false;
              if (this.isCEF) {
                this.dialogService.openDialog(Messages.NO_FACTORS_AVAILABLE, () => this.routingService.navigateToHome());
                return;
              }
              this.dialogService.openDialog(Messages.NO_FACTORS_AVAILABLE, () => this.back());
            }
          },
          (err: HttpErrorResponse) => {
            this.mdrFlexLoading = false;
            this.mdrFlexLoaded = false;
            const serializedError = err.error ? (err.error as ApiResultModel) : null;
            if (serializedError && serializedError.message) {
              this.dialogService.openDialogWithMessage(serializedError.message, () => this.back());
            } else {
              this.dialogService.openDialog(Messages.FAILED_TO_GET_MDR_FLEX_FACTORS, () => this.back());
            }
          }
        );
    }
  }

  public persistData() {
    const rawValue = this.formGroup.getRawValue();
    const mdrFlexFeeModel: MdrFlexFeeModel[] = rawValue.mdrFlexFeeForms;
    const factorFeeModel: FactorFeeModel[] = rawValue.factorFeeForms;
    const feeTypeModel: FeeTypeModel = this.proposal?.feeTypeModel ? { ...this.proposal.feeTypeModel } : null;
    if (this.isPfacAccreditationType() && feeTypeModel) {
      feeTypeModel.receivingDeadline = rawValue.receivingDeadline;
    }
    return this.dataStore.updateProposal({ mdrFlexFeeModel, factorFeeModel, feeTypeModel });
  }

  public openFactorFeeEditorModal(dialogData: FeeEditorDialogData, factorIndex: number) {
    const relatedFormGroup = this.factorFeeFormsArray.controls[factorIndex] as UntypedFormGroup;

    if (relatedFormGroup) {
      const rawValue = relatedFormGroup.getRawValue();
      if (rawValue && rawValue.suggestedFactorFee !== null && rawValue.suggestedFactorFee !== undefined) {
        const item = this.factorFees.find((val, idx) => idx === factorIndex);
        if (item) {
          dialogData.title = dialogData.field.modalTitle;
          dialogData.itemDescription = `${item.flag} - ${item.transaction}`;
          dialogData.initialValue = dialogData.formControl.value ?? item.suggestedFactorFee;
          dialogData.maxValue = item.suggestedFactorFee;
          dialogData.minValue = item.minimumFactorFee;
          dialogData.rowIndex = factorIndex;
          dialogData.confirmCallbackFn = this.onFactorModalConfirm.bind(this);
          this.dialog.open(FeeEditorModalComponent, {
            disableClose: true,
            width: ModalDefinitions.DEFAULT_MODAL_WIDTH,
            panelClass: 'fd-fee-editor-dialog',
            data: dialogData,
          });
          return;
        }
      }
    }
  }

  public isFactorEdited(itemParam: Partial<FactorFeeModel>) {
    if (itemParam) {
      const arrItem = this.factorFees.find(
        (val) => val.flag === itemParam.flag && val.factor === itemParam.factor && val.transaction === itemParam.transaction
      );
      if (!itemParam || !itemParam.suggestedFactorFee || !arrItem || !arrItem.suggestedFactorFee) return false;
      return itemParam.suggestedFactorFee !== arrItem.suggestedFactorFee;
    }
  }

  public isFactorEditedWithValue(itemParam: Partial<FactorFeeModel>, value: number) {
    if (itemParam) {
      const arrItem = this.factorFees.find(
        (val) => val.factor === itemParam.factor && val.flag === itemParam.flag && val.transaction === itemParam.transaction
      );
      return value !== arrItem.suggestedFactorFee;
    }
  }

  public isMdrFlexEdited(itemParam: Partial<MdrFlexFeeModel>) {
    if (itemParam) {
      const arrItem = this.mdrFlexFees.find((val) => val.codeFlag === itemParam.codeFlag && val.modality === itemParam.modality);
      if (!itemParam || !itemParam.suggestedMdrFlexFee || !arrItem || !arrItem.suggestedMdrFlexFee) return false;
      return itemParam.suggestedMdrFlexFee !== arrItem.suggestedMdrFlexFee;
    }
  }

  public openMdrFlexFeeEditorModal(dialogData: FeeEditorDialogData, mdrFlexIndex: number) {
    const relatedFormGroup = this.mdrFlexFeeFormsArray.controls[mdrFlexIndex] as UntypedFormGroup;

    if (relatedFormGroup) {
      const rawValue = relatedFormGroup.getRawValue();
      if (rawValue && rawValue.suggestedMdrFlexFee !== null && rawValue.suggestedMdrFlexFee !== undefined) {
        const item = this.mdrFlexFees.find((val, idx) => idx === mdrFlexIndex);
        if (item) {
          dialogData.title = dialogData.field.modalTitle;
          dialogData.itemDescription = `${item.flag} - ${item.transaction}`;
          dialogData.maxValue = item.suggestedMdrFlexFee;
          dialogData.minValue = item.minimumMdrFlexFee;
          dialogData.initialValue = this.getEditedOrSuggestedFee(dialogData, item);
          dialogData.rowIndex = mdrFlexIndex;
          dialogData.confirmCallbackFn = this.onMdrFlexModalConfirm.bind(this);
          const dialogRef = this.dialog.open(FeeEditorModalComponent, {
            disableClose: true,
            width: ModalDefinitions.DEFAULT_MODAL_WIDTH,
            panelClass: 'fd-fee-editor-dialog',
            data: dialogData,
          });
          dialogRef.afterClosed().subscribe(() => (this.dialogClosed = true));
          dialogRef.afterOpened().subscribe(() => (this.dialogClosed = false));
          return;
        }
      }
    }
  }

  public getEditedOrSuggestedFee(dialogData: FeeEditorDialogData, item: MdrFlexFeeModel) {
    if (dialogData && dialogData.formControl) return dialogData.formControl.value;
    else if (item) return item.suggestedMdrFlexFee;
    else return null;
  }

  public createFields() {
    return {
      receivingDeadline: {
        controlName: 'receivingDeadline',
        label: 'Qual é o prazo de liquidação?',
        items: [
          { value: 30, label: '30 dias' },
          { value: 31, label: '31 dias' },
        ],
        messages: {
          required: 'Informe o prazo de liquidação',
          invalid: 'Prazo de liquidação inválido',
        },
      } as FdRadioButtonConfig,
      suggestedFactorFee: {
        controlName: 'suggestedFactorFee',
        maskCharsReplace: /[% ]/,
        modalTitle: 'Indique a taxa do produto',
        childControl: {
          controlName: 'factorFeeEdit',
          label: 'Taxa solicitada',
          isPercent: true,
          maxLength: 4,
          messages: {
            required: 'Informe o valor',
            invalid: 'Valor inválido',
            valueTooLow: 'O valor deve ser maior que o mínimo especificado',
            valueTooHigh: 'O valor deve ser menor que o máximo especificado',
          },
        },
      },
      suggestedMdrFlexFee: {
        controlName: 'suggestedMdrFlexFee',
        maskCharsReplace: /[% ]/,
        modalTitle: 'Indique a taxa do produto',
        childControl: {
          controlName: 'mdrFlexFeeEdit',
          label: 'Taxa solicitada',
          isPercent: true,
          maxLength: 4,
          messages: {
            required: 'Informe o valor',
            invalid: 'Valor inválido',
            valueTooLow: 'O valor deve ser maior que o mínimo especificado',
            valueTooHigh: 'O valor deve ser menor que o máximo especificado',
          },
        },
      },
      calculatedMdrFlexFee: {
        controlName: 'calculatedMdrFlexFee',
        maskCharsReplace: /[% ]/,
        modalTitle: 'Indique a taxa do produto',
        childControl: {
          controlName: 'mdrFlexFeeEdit',
          label: 'Taxa solicitada',
          isPercent: true,
          maxLength: 4,
          messages: {
            required: 'Informe o valor',
            invalid: 'Valor inválido',
            valueTooLow: 'O valor deve ser maior que o mínimo especificado',
            valueTooHigh: 'O valor deve ser menor que o máximo especificado',
          },
        },
      },
    };
  }

  public addFactorFeeForm(factors: Partial<FactorFeeModel>[] = [{}]) {
    factors.forEach((factor) => {
      this.fieldsArray.push(this.createFields());
      this.factorFeeFormsArray.push(this.createFactorForm(factor));
    });
  }

  public addMdrFlexFeeForm(fees: Partial<MdrFlexFeeModel>[] = [{}]) {
    this.fieldsArray = [];
    this.mdrFlexFeeFormsArray = this.formBuilder.array([]);
    fees.forEach((item, idx) => {
      this.fieldsArray.push(this.createFields());
      this.mdrFlexFeeFormsArray.push(this.createMdrFlexForm(item));
      (this.fieldsArray[idx].suggestedMdrFlexFee as FdFeeEditorConfig).isEdited = this.isMdrFlexEdited(item);
    });
  }

  public clearMdrFlexFormArray() {
    (this.formControls as any).mdrFlexFeeForms = this.formBuilder.array([]);
  }

  public calculateFees(feesToCalculate: FeeCalculationModel[], mdrFlexFeeModel: MdrFlexFeeModel[]) {
    this.feeService
      .calculateFees(
        feesToCalculate,
        this.proposal.offerDataDetails.cnae,
        this.proposal.registrationDataAddress.state,
        this.proposal.offerDataDetails.revenueRange,
        this.proposal.offerDataDetails.campaign,
        this.proposal.feeTypeModel.receivingDeadline,
        this.proposal.registrationDataPersonalInfo.documentCategory === 'CPF' ? 'P' : 'L'
      )
      .pipe(takeWhile(() => this.alive))
      .subscribe(
        (calculated) => {
          calculated.forEach((calc) => {
            const item = mdrFlexFeeModel.find((model) => calc.codeFlag === model.codeFlag && calc.modality === model.modality);
            if (item) item.calculatedMdrFlexFee = calc.fee;
          });

          this.mdrFlexDataSource.data = mdrFlexFeeModel;
          this.mdrFlexGroupedDataSource.data = this.groupMdrFlexFees(mdrFlexFeeModel);
          this.refreshDisplayedMdrFlexColumns();
          this.addMdrFlexFeeForm(mdrFlexFeeModel);

          this.mdrFlexLoaded = (this.mdrFlexGroupedDataSource.data?.length || 0) > 0;
          this.mdrFlexLoading = false;
        },
        (err: HttpErrorResponse) => {
          this.mdrFlexLoading = false;
          this.mdrFlexLoaded = false;

          this.mdrFlexGroupedDataSource.data = [];
          this.refreshDisplayedMdrFlexColumns();

          const serializedError = err.error ? (err.error as ApiResultModel) : null;
          if (serializedError && serializedError.message) {
            if (this.isCEF) {
              this.dialogService.openDialogWithMessage(serializedError.message, () => this.routingService.navigateToHome());
              return;
            }
            this.dialogService.openDialogWithMessage(serializedError.message, () => this.back());
          } else {
            if (this.isCEF) {
              this.dialogService.openDialog(Messages.FAILED_TO_CALCULATE_FEES, () => this.routingService.navigateToHome());
              return;
            }
            this.dialogService.openDialog(Messages.FAILED_TO_CALCULATE_FEES, () => this.back());
          }
        }
      );
  }

  public getMdrFlexFees() {
    this.feeService
      .getMdrFlexFees(
        this.proposal.offerDataDetails.cnae,
        this.proposal.registrationDataAddress.state,
        this.proposal.offerDataDetails.campaign,
        this.proposal.offerDataDetails.revenueRange,
        this.proposal.feeTypeModel.receivingDeadline,
        this.proposal.registrationDataPersonalInfo.documentCategory === 'CPF' ? 'P' : 'L'
      )
      .pipe(takeWhile(() => this.alive))
      .subscribe(
        (response) => {
          if (response && response.length) {
            this.processMdrFlexFees(response);
          } else {
            this.mdrFlexLoading = false;
            this.mdrFlexLoaded = false;
            this.mdrFlexGroupedDataSource.data = [];
            this.refreshDisplayedMdrFlexColumns();
            if (this.isCEF) {
              this.dialogService.openDialog(Messages.FAILED_TO_GET_MDR_FLEX_FEES, () => this.routingService.navigateToHome());
              return;
            }
            this.dialogService.openDialog(Messages.FAILED_TO_GET_MDR_FLEX_FEES, () => this.back());
          }
        },
        (err: HttpErrorResponse) => {
          this.mdrFlexLoading = false;
          this.mdrFlexLoaded = false;

          this.mdrFlexGroupedDataSource.data = [];
          this.refreshDisplayedMdrFlexColumns();

          const serializedError = err.error ? (err.error as ApiResultModel) : null;
          if (serializedError && serializedError.message) {
            if (this.isCEF) {
              this.dialogService.openDialogWithMessage(serializedError.message, () => this.back());
              return;
            }
            this.dialogService.openDialogWithMessage(serializedError.message, () => this.back());
          } else {
            if (this.isCEF) {
              this.dialogService.openDialog(Messages.FAILED_TO_GET_MDR_FLEX_FEES, () => this.routingService.navigateToHome());
              return;
            }
            this.dialogService.openDialog(Messages.FAILED_TO_GET_MDR_FLEX_FEES, () => this.back());
          }
        }
      );
  }

  public processMdrFlexFees(response: MdrFlexFeeModel[]) {
    const feesToCalculate: FeeCalculationModel[] = [];
    response.forEach((item) => {
      if (item.feeType == 'CURRENCY' && this.isCnaeLoterica) {
        item.tariffValue = item.suggestedMdrFlexFee;
      }
      feesToCalculate.push({
        codeFlag: item.codeFlag,
        factor1: this.factor1,
        factor2: this.factor2,
        fee: item.suggestedMdrFlexFee,
        modality: item.modality,
      });
    });

    this.mdrFlexFees = response;
    this.calculateFees(feesToCalculate, response);
  }

  public getValueWithDecimalPlaces(value: any) {
    return getValueWithDecimalPlaces(value);
  }

  public processFactorFees(response: FactorFeeModel[]) {
    response.forEach((factorFee) => {
      if (factorFee.factor === FactorIndexEnum.FACTOR_ONE) this.factor1 = factorFee.suggestedFactorFee;
      else if (factorFee.factor === FactorIndexEnum.FACTOR_TWO) this.factor2 = factorFee.suggestedFactorFee;
    });
    this.factorDataSource.data = response;
    this.addFactorFeeForm(response);
  }

  public async ngOnInit() {
    await super.ngOnInit();
  }

  public canEdit(modality: MdrFlexModalityEnum): boolean {
    return modality !== MdrFlexModalityEnum.ADDITIONAL_PER_INSTALLMENT;
  }

  public canEditGroupedCell(cell?: CellRef): boolean {
    if (!cell || !cell.model) return false;
    return this.canEdit(cell.model.modality);
  }

  public next(): void {
    this.submitted = true;
    if (this.dataStoreService.isBinServiceContractProposal(this.proposal.serviceContract)) {
      const data: RatesClientValidation = this.buildValidationRatesData();
      this.clientService.validateRates(data).subscribe(
        (response) => {
          if (!response) this.dialogService.openDialog(Messages.RATES_INVALID_SIPAG);
          else super.next();
        },
        (err) => {
          let message = '';
          let obj: any = err.error.details;
          if (err.error && err.error.details) {
            if (obj instanceof Object) {
              const details = err.error.details;
              if (details && details.mensagem) {
                message += details.mensagem;
                obj = '';
              } else {
                message += JSON.stringify(err.error.details);
              }
            } else {
              message += obj;
            }
          } else if (err.error && err.error.message) {
            message += err.error.message;
          }
          if (message.length > 0) {
            if (obj instanceof Object) {
              if (this.isCEF) {
                this.dialogService.openErrorDialog(Messages.GENERIC_ERROR, JSON.stringify(err.error.details), () =>
                  this.routingService.navigateToHome()
                );
                return;
              }
              this.dialogService.openErrorDialog(Messages.GENERIC_ERROR, JSON.stringify(err.error.details));
            } else {
              if (this.isCEF) {
                this.dialogService.openDialogWithMessage(message, () => this.routingService.navigateToHome());
                return;
              }
              this.dialogService.openDialogWithMessage(message);
            }
          } else if (this.isCEF) {
            this.dialogService.openDialog(Messages.GENERAL_ERROR, () => this.routingService.navigateToHome());
            return;
          }
          this.dialogService.openDialog(Messages.GENERAL_ERROR);
        }
      );
    } else {
      super.next();
    }
  }

  public buildValidationRatesData(): RatesClientValidation {
    const ratesList: Rate[] = new Array();
    const rawValue = this.formGroup.getRawValue();
    const mdrFlexFeeModel: MdrFlexFeeModel[] = rawValue.mdrFlexFeeForms;
    mdrFlexFeeModel.forEach((mdrFlex) => {
      const rate: Rate = {
        acquiringServiceId: mdrFlex.serviceId ? mdrFlex.serviceId.toString() : undefined,
        codeFlag: mdrFlex.codeFlag,
        modality: mdrFlex.modality,
        rate: mdrFlex.calculatedMdrFlexFee ? mdrFlex.calculatedMdrFlexFee.toString() : mdrFlex.suggestedMdrFlexFee.toString(),
      };
      ratesList.push(rate);
    });
    const data: RatesClientValidation = {
      cnae: this.proposal.offerDataDetails.cnae,
      document: this.proposal.registrationDataPersonalInfo.cpfCnpj,
      rateCategory: FeeTypeEnum.FLEX,
      subChannel: this.proposal.subChannel,
      ratesList,
    };
    return data;
  }

  protected proposalGetCallback(): void {}

  protected createFormGroup() {
    return this.formBuilder.group({
      factorFeeForms: this.formBuilder.array([]),
      mdrFlexFeeForms: this.formBuilder.array([]),
      receivingDeadline: ['', this.isPfacAccreditationType() ? Validators.required : null],
    });
  }

  private createFactorForm(fee: Partial<FactorFeeModel>): UntypedFormGroup {
    return this.formBuilder.group({
      suggestedFactorFee: [fee.suggestedFactorFee],
      factorFeeEdit: [''],
      minimumFactorFee: [fee.minimumFactorFee],
      factor: [fee.factor],
      transaction: [fee.transaction],
      flag: [fee.flag],
    });
  }

  private createMdrFlexForm(fee: Partial<MdrFlexFeeModel>): UntypedFormGroup {
    return this.formBuilder.group({
      suggestedMdrFlexFee: [fee.suggestedMdrFlexFee],
      calculatedMdrFlexFee: [fee.calculatedMdrFlexFee],
      mdrFlexFeeEdit: [fee.suggestedMdrFlexFee],
      codeFlag: [fee.codeFlag],
      flag: [fee.flag],
      minimumMdrFlexFee: [fee.minimumMdrFlexFee],
      transaction: [fee.transaction],
      modality: [fee.modality],
      serviceId: [fee.serviceId],
      feeType: [fee.feeType],
      anticipationVoucherDisabled: [fee.anticipationVoucherDisabled],
      completeVoucherSubscription: [fee.completeVoucherSubscription],
    });
  }

  private refreshDisplayedMdrFlexColumns(): void {
    const shouldShowVoucher = this.hasVoucher;

    const next = shouldShowVoucher ? [...this.BASE_MDR_FLEX_COLUMNS, 'voucher'] : [...this.BASE_MDR_FLEX_COLUMNS];

    const changed = next.length !== this.displayedMdrFlexColumns.length || next.some((c, i) => c !== this.displayedMdrFlexColumns[i]);

    if (changed) {
      this.displayedMdrFlexColumns = next;
    }
  }

  private tooltipVoucher: string;
  public hasVoucherMsg: boolean = true;

  public getTextOfModalVoucher(): string {
    if (this.tooltipVoucher && this.tooltipVoucher.length) {
      return this.tooltipVoucher;
    }
    this.hasVoucherMsg = false;
    const tooltipMsg = [];
    const fees = this.mdrFlexGroupedDataSource?.data || [];

    for (const fee of fees) {
      if (fee.voucher) {
        if (fee.voucher.model.anticipationVoucherDisabled) {
          tooltipMsg.push(Constants.VOUCHER_AINTICIPATION_DISABLE_TOOLTIP_MSG);
          this.hasVoucherMsg = true;
        }
        if (fee.voucher.model.completeVoucherSubscription) {
          tooltipMsg.push(Constants.VOUCHER_COMPLETE_SUBSCRIPTION_TOOLTIP_MSG);
          this.hasVoucherMsg = true;
        }
        break;
      }
    }
    this.tooltipVoucher = tooltipMsg.join('\n\n');
    return this.tooltipVoucher;
  }

  private modalityToColumn(modality: any): keyof GroupedMdrFlexRow | null {
    if (typeof modality === 'number') {
      if (modality === (MdrFlexModalityEnum as any).CREDIT) return 'credito';
      if (modality === (MdrFlexModalityEnum as any).DEBIT) return 'debito';
      if (modality === (MdrFlexModalityEnum as any).LESS_THAN_OR_EQUAL_TO_SIX_INSTALLMENTS) return 'parceladoAte6';
      if (modality === (MdrFlexModalityEnum as any).BIGGER_THAN_SIX_INSTALLMENTS) return 'parceladoMais6';
      if (modality === (MdrFlexModalityEnum as any).CREDIARIO) return 'crediario';
      if (modality === (MdrFlexModalityEnum as any).VOUCHER || modality === (MdrFlexModalityEnum as any).VOUCHER) return 'voucher';

      return null;
    }

    const val = (typeof modality === 'string' ? modality : String(modality)).toUpperCase();
    if (val === 'CREDIT' || val === 'CREDITO' || val === 'CARTAO_CREDITO') return 'credito';
    if (val === 'DEBIT' || val === 'DEBITO' || val === 'CARTAO_DEBITO') return 'debito';
    if (val === 'LESS_THAN_OR_EQUAL_TO_SIX_INSTALLMENTS' || val === 'PARCELADO_ATE_6') return 'parceladoAte6';
    if (val === 'BIGGER_THAN_SIX_INSTALLMENTS' || val === 'PARCELADO_MAIS_6') return 'parceladoMais6';
    if (val === 'CREDIARIO') return 'crediario';
    if (val === 'VOUCHER') return 'voucher';

    return null;
  }

  private isAdditionalPerInstallmentModality(modality: any): boolean {
    if (typeof modality === 'number') {
      return modality === (MdrFlexModalityEnum as any).ADDITIONAL_PER_INSTALLMENT;
    }
    const val = (typeof modality === 'string' ? modality : String(modality)).toUpperCase();
    return val === 'ADDITIONAL_PER_INSTALLMENT';
  }

  private groupMdrFlexFees(model: MdrFlexFeeModel[]): GroupedMdrFlexRow[] {
    this.additionalPerInstallment = null;
    this.additionalPerInstallmentIndex = null;

    model.forEach((m, idx) => {
      if (this.isAdditionalPerInstallmentModality(m.modality)) {
        this.additionalPerInstallment = m;
        this.additionalPerInstallmentIndex = idx;
      }
    });

    const map = new Map<string, GroupedMdrFlexRow>();
    model.forEach((m, idx) => {
      if (this.isAdditionalPerInstallmentModality(m.modality)) return;

      const key = `${String(m.codeFlag)}|${m.flag}`;
      if (!map.has(key)) map.set(key, { codeFlag: m.codeFlag, flag: m.flag });

      const row = map.get(key)!;
      const col = this.modalityToColumn(m.modality);
      if (col) (row as any)[col] = { index: idx, model: m } as CellRef;
    });

    const rows = Array.from(map.values());
    let additionalRow: GroupedMdrFlexRow | null = null;

    if (this.additionalPerInstallment && this.additionalPerInstallmentIndex !== null) {
      additionalRow = {
        flag: 'Todas',
        codeFlag: String(this.additionalPerInstallment.codeFlag),
        parceladoAte6: { index: this.additionalPerInstallmentIndex, model: this.additionalPerInstallment },
        parceladoMais6: { index: this.additionalPerInstallmentIndex, model: this.additionalPerInstallment },
      };
    }

    const FLAG_ORDER = ['VISA', 'MASTER', 'ELO', 'CABAL', 'AMEX'];

    const canonicalFlagName = (raw: string | undefined | null): string => {
      const s = (raw || '').trim().replace(/\s+/g, ' ').toUpperCase();
      return s;
    };

    const priorityIndex = (flag: string): number => {
      const c = canonicalFlagName(flag);
      const idx = FLAG_ORDER.indexOf(c);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };

    rows.sort((a, b) => {
      const pa = priorityIndex(a.flag);
      const pb = priorityIndex(b.flag);
      if (pa !== pb) return pa - pb;
      return (a.flag || '').localeCompare(b.flag || '', undefined, { sensitivity: 'base' });
    });

    if (additionalRow) rows.push(additionalRow);

    return rows;
  }

  public get hasVoucher(): boolean {
    const data = this.mdrFlexGroupedDataSource?.data || [];
    return data.some((r) => !!r.voucher);
  }

  getFlagImageURL(flagName?: string) {
    if (!flagName) return '';
    return '/assets/images/simulator-flags/' + flagName.toUpperCase() + '.png';
  }
}
